import classNames from "components/ui/classNames";
import { RadioGroup, RadioGroupItem } from "components/ui/RadioGroup";
import type { SeriesAreaOptions, SeriesColumnOptions, SeriesPieOptions } from "highcharts";
import { ActivityIcon, BarChartIcon } from "lucide-react";
import type { ReactElement } from "react";
import { useIntl } from "react-intl";

export type PlotType = SeriesAreaOptions["type"] | SeriesColumnOptions["type"] | SeriesPieOptions["type"] | "table";
type ChartPlotType<T> = {
  type: T;
  label: string;
  icon?: ReactElement;
  tooltipContent?: string;
  disabled?: boolean;
};

interface PlotTypeSelectorProps<T extends string> {
  types: ChartPlotType<T>[];
  value: T;
  onChange: (plotType: T) => void;
  className?: string;
  itemClassName?: string;
}
export default function PlotTypeSelector<T extends string>({
  types,
  value,
  onChange,
  className,
  itemClassName,
}: PlotTypeSelectorProps<T>) {
  return (
    <RadioGroup
      loop={false}
      className={classNames(
        "grid w-full justify-between rounded-2xl border border-slate-200 bg-slate-50/60 p-1",
        "sm:flex sm:w-auto sm:max-w-32 sm:justify-normal sm:space-x-2",
        "dark:border-slate-950 dark:bg-slate-700 dark:text-slate-300",
        className,
        {
          "grid-cols-1": types.length === 1,
          "grid-cols-2": types.length === 2,
          "grid-cols-3": types.length === 3,
          "grid-cols-4 sm:max-w-40": types.length === 4,
        },
      )}
      orientation="horizontal"
      value={value}
      onValueChange={(value) => {
        const match = types.find((candidate) => candidate.type === value);
        if (match) {
          onChange(match.type);
        }
      }}
    >
      {types.map(({ type, label, icon, tooltipContent, disabled }) => {
        return (
          <RadioGroupItem
            key={type}
            value={type}
            disabled={disabled}
            title={tooltipContent || label}
            className={classNames(
              "h-9 w-full items-center justify-center rounded-xl sm:w-10",
              "border border-transparent text-sm font-medium tracking-wide text-slate-500 dark:text-slate-400",
              "data-[state=checked]:bg-violet-50 data-[state=checked]:font-bold data-[state=checked]:text-violet-600",
              "data-[state=checked]:dark:border-slate-800 data-[state=checked]:dark:bg-violet-600/30 data-[state=checked]:dark:text-violet-200",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-100 focus-visible:ring-offset-1 focus-visible:ring-offset-gray-300",
              "data-[state=checked]:focus-visible:rounded-xl data-[state=checked]:focus-visible:bg-gray-50 data-[state=checked]:focus-visible:text-gray-600",
              itemClassName,
            )}
          >
            {icon || label}
          </RadioGroupItem>
        );
      })}
    </RadioGroup>
  );
}

export function AreaBarPlotTypeSelector(props: Omit<PlotTypeSelectorProps<"area" | "column">, "types">) {
  const intl = useIntl();
  return (
    <PlotTypeSelector
      types={[
        {
          type: "area",
          label: intl.formatMessage({
            id: "oaRYWa",
            defaultMessage: "Linha",
            description: "Tipo de visualização do gráfico",
          }),
          icon: <ActivityIcon className="h-5 w-5" />,
        },
        {
          type: "column",
          label: intl.formatMessage({
            id: "hZ073v",
            defaultMessage: "Barra",
            description: "Tipo de visualização do gráfico",
          }),
          icon: <BarChartIcon className="h-5 w-5" />,
        },
      ]}
      {...props}
    />
  );
}
